import styled, { keyframes } from 'styled-components';
import { Button } from '../BackButton/BackButton.styles';

const breakpoints = {
  large: '1200px',
  xlarge: '1440px',
  xxlarge: '1800px',
};

// Article width including its 4rem side padding, so the actual text measure
// lands around 65-72 characters -- the comfortable range for long-form reading.
const maxWidths = {
  default: '800px',
  large: '848px',
  xlarge: '860px',
  xxlarge: '880px',
};

const responsiveMaxWidth = `
  max-width: ${maxWidths.default};

  @media (min-width: ${breakpoints.large}) {
    max-width: ${maxWidths.large};
  }

  @media (min-width: ${breakpoints.xlarge}) {
    max-width: ${maxWidths.xlarge};
  }

  @media (min-width: ${breakpoints.xxlarge}) {
    max-width: ${maxWidths.xxlarge};
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Shimmer animation for loading states - used in BlogContent skeleton loaders
// const shimmer = keyframes`
//   0% { background-position: -1000px 0; }
//   100% { background-position: 1000px 0; }
// `;

export const Article = styled.article`
  ${responsiveMaxWidth}
  width: 100%;
  min-width: 0; /* let the column shrink beside the TOC and contain wide code blocks */
  margin: 0; /* centering is handled by BlogLayout */
  padding: 4rem;
  border-radius: 10px;
  line-height: 1.7;
  font-size: 1.1rem;
  animation: ${fadeIn} 500ms ease-in;

  @media (max-width: 768px) {
    padding: 0;
    font-size: 1rem;
  }
`;

/* Centers the reading column and parks the table of contents in the left
   gutter on wide screens. With no TOC the column simply centers. */
export const BlogLayout = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 3rem;
  width: 100%;
`;

export const ImageContainer = styled.div`
  width: 100%;
  height: 500px;
  background-image: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;
  position: relative;
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.01);
  }
`;

export const TitleOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: white;
  background: linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%);
  border-radius: 16px;

  h1 {
    width: 100%;
    text-align: center;
    font-size: 3rem;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
`;

export const MetaData = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2rem 0;
  padding: 1rem 0;
  border-top: 1px solid #edf2f7;
  border-bottom: 1px solid #edf2f7;
  font-size: 0.95rem;
  color: #64748b;
`;

export const BlogInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 3rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

export const ReadingTime = styled.div`
  color: rgb(93, 112, 246);
  font-weight: 500;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
  position: relative;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
`;

export const TopInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Categories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    margin-top: 0.5rem;
  }
`;

export const Category = styled.span`
  color: rgb(93, 112, 246);
  font-weight: 500;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
  position: relative;
  transition: all 0.2s ease;
  
  &:not(:last-child)::after {
    content: '•';
    color: #999;
    margin-left: 1rem;
  }
`;

export const BlogHeadline = styled.h1`
  color: #333;
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 0.5rem;
  
  @media (max-width: 860px) {
    font-size: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

export const AuthorInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 2rem;
  width: 100%;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(to right, rgba(93, 112, 246, 0.7), rgba(147, 112, 219, 0.5), transparent);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    
    & > div:last-child {
      width: 100%;
      margin-top: 0.5rem;
      justify-content: flex-start;
    }
  }
`;

export const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

export const AuthorImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid white;
`;

export const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  
  span:first-child {
    font-weight: 600;
    color: #333;
    font-size: 1rem;
  }
  
  span:last-child {
    color: #777;
    font-size: 0.9rem;
  }
`;

export const AccessibilityControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

export const Content = styled.div`
  /* Tighter than before so lines within a paragraph read as one block.
     The airy feel now comes from the paragraph GAP below, not loose leading. */
  line-height: 1.7;
  margin: 0 auto;
  color: #2a2a2e; /* near-black body: more substantial than the old faint #555 */
  font-size: 1.125rem; /* ~18px article size */
  font-weight: 400;
  font-family: 'Outfit', sans-serif; /* matches the rest of the site */

  p {
    font-size: 1.125rem;
    line-height: 1.75;
    /* Clear gap on every breakpoint so paragraphs group as distinct blocks
       instead of every line floating at the same rhythm. */
    margin: 0 0 1.65rem;
  }

  /* Editorial lead-in: the opening paragraph sets the tone, a touch larger
     and darker -- the way real publications open an article. */
  & > p:first-child {
    font-size: 1.35rem;
    line-height: 1.6;
    color: #1a1a1f;
    margin-bottom: 1.9rem;
  }

  /* Keep related lines together: don't let a stray paragraph gap dangle. */
  p:last-child {
    margin-bottom: 0;
  }

  h2, h3, h4 {
    /* More space ABOVE than below: a heading separates from the previous
       section and binds to the paragraph it introduces. */
    margin-top: 2.75rem;
    margin-bottom: 0.85rem;
    line-height: 1.25;
    letter-spacing: -0.015em;
    color: #1a1a1f;
    font-weight: 700;
    scroll-margin-top: 110px; /* anchor target clears the fixed site header */
  }
  
  h2 {
    font-size: 2rem;
  }

  h3 {
    font-size: 1.55rem;
  }

  h4 {
    font-size: 1.25rem;
  }

  blockquote {
    border: 1px solid #e6e6e6;
    border-left: 3px solid rgb(93, 112, 246);
    background: #fff;
    border-radius: 0.5rem;
    padding: 1.5rem 2rem;
    margin: 2rem 0 1.75rem;
    position: relative;
    color: #3a3a40;
    font-weight: 400;
    
    &::before {
      content: '"';
      position: absolute;
      top: 10px;
      left: 12px;
      font-size: 3rem;
      line-height: 1;
      color: rgb(93, 112, 246);
      font-family: Georgia, serif;
    }
    
    p {
      margin-bottom: 0.75rem;
      font-size: 1.05rem;
      line-height: 1.7;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    cite {
      display: block;
      margin-top: 1rem;
      font-size: 0.9rem;
      font-weight: 500;
      color: #888;
      font-style: normal;
      
      &::before {
        content: '— ';
      }
    }
  }

  img {
    max-width: 100%;
    height: auto;
    margin: 1rem auto;
    display: block;
  }

  a {
    color: rgb(93,112,246);
    text-decoration: underline;
    text-decoration-color: rgba(93,112,246,0.35);
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    transition: text-decoration-color 0.2s ease;

    & strong {
      color: rgb(93,112,246);
    }

    &:hover {
      text-decoration-color: rgb(93,112,246);
    }
  }

  /* List styling with better spacing */
  ul, ol {
    margin: 1.5rem 0;
    padding-left: 1.8rem;
  }

  ul {
    list-style-type: none;
  }

  ul li {
    position: relative;
    padding-left: 0.5rem;
  }

  ul li::before {
    content: "•";
    color: rgb(93, 112, 246);
    font-size: 1.2em;
    position: absolute;
    left: -1rem;
    top: -0.1rem;
  }

  ol {
    counter-reset: list-counter;
    list-style-type: none;
  }

  ol li {
    counter-increment: list-counter;
    position: relative;
    padding-left: 0.5rem;
  }

  ol li::before {
    content: counter(list-counter) ".";
    color: rgb(93, 112, 246);
    position: absolute;
    left: -1.5rem;
    font-weight: 500;
  }

  li {
    margin-bottom: 0.45rem;
    line-height: 1.7;
  }

  strong {
    font-weight: 600; /* Less bold than default */
    color: #444;
  }

  em {
    color: #555;
  }

  @media (min-width: 1024px) {
    font-size: 1.15rem; /* a hair larger on wide screens, never below the base */
    margin: 0 auto;

    p {
      font-size: 1.15rem;
    }
  }

  @media (max-width: 768px) {
    line-height: 1.65;

    p {
      font-size: 1.05rem;
      margin-bottom: 1.4rem;
    }

    & > p:first-child {
      font-size: 1.2rem;
    }
  }
`;

export const TableOfContents = styled.nav`
  /* Hidden by default; only appears when there is room in the gutter. */
  display: none;
  flex: 0 0 220px;
  position: sticky;
  top: 120px; /* clears the fixed site header */
  align-self: flex-start;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
  padding: 0.25rem 0 0.25rem 1.25rem;
  border-left: 1px solid #e6e6e6;
  font-family: "Outfit", sans-serif;

  @media (min-width: 1200px) {
    display: block;
  }

  .toc-title {
    margin: 0 0 0.85rem;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8a8a93;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin: 0;
    padding: 0;

    &.toc-sub a {
      padding-left: 1rem;
      font-size: 0.82rem;
      color: #777;
    }
  }

  a {
    display: block;
    padding: 0.3rem 0;
    color: #555;
    font-size: 0.9rem;
    line-height: 1.35;
    text-decoration: none;
    border-left: 2px solid transparent;
    margin-left: -1.25rem;
    padding-left: 1.25rem;
    transition: color 0.2s ease, border-color 0.2s ease;

    &:hover {
      color: rgb(93, 112, 246);
    }

    &.active {
      color: rgb(93, 112, 246);
      font-weight: 600;
      border-left-color: rgb(93, 112, 246);
    }
  }
`;

export const ReadNextSection = styled.section`
  ${responsiveMaxWidth}
  margin: 4rem auto 0;
  padding: 0 4rem;
  animation: ${fadeIn} 500ms ease-in;

  @media (max-width: 768px) {
    padding: 0;
    margin-top: 3rem;
  }
`;

export const ReadNextHeading = styled.h2`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: #1a1a1f;
  margin-bottom: 1.5rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #e6e6e6;
`;

export const ReadNextGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
`;

export const ProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 0; /* width is set imperatively via ref on scroll (no re-render) */
  height: 3px;
  background: linear-gradient(90deg, rgb(93, 112, 246), rgb(93, 112, 246));
  z-index: 1000;
`;

export const ShareSection = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  border-radius: 16px;
  animation: ${fadeIn} 500ms ease-in;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

export const ShareText = styled.p`
  font-size: 1.25rem;
  color: #333;
  font-weight: 500;
  margin: 0;
`;

export const ShareButtonStyled = styled(Button)`
  position: relative;
  top: unset;
  left: unset;
  bottom: unset;
  right: unset;
  background: #000;
  color: white;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 12px #00000030;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px #00000030;
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    margin-right: 8px;
    vertical-align: middle;
  }

  @media (max-width: 768px) {
    position: unset;
    padding: 0.75rem 1rem;
  }
`;

export const TextToSpeechButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.isSpeaking ? 'rgb(93, 112, 246)' : '#ffffff'};
  color: ${props => props.isSpeaking ? 'white' : '#333'};
  border: 1px solid ${props => props.isSpeaking ? 'rgb(93, 112, 246)' : '#e6e6e6'};
  border-radius: 1.25rem;
  
  &:hover {
    background-color: ${props => props.isSpeaking ? '#5d70f6' : '#e9e9e9'};
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;

export const VoiceOptionsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #e9e9e9;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

export const VoiceOptionsPanel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  min-width: 250px;
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  
  h4 {
    margin-top: 0;
    margin-bottom: 1rem;
    color: #333;
    font-size: 1rem;
    font-weight: 600;
  }
  
  select {
    width: 100%;
    padding: 0.5rem;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    font-size: 0.9rem;
    background-color: #f9f9f9;
    color: #333;
    margin-top: 0.3rem;
  }
  
  .slider-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.3rem;
    
    input[type="range"] {
      flex-grow: 1;
    }
    
    span {
      min-width: 2rem;
      text-align: right;
      font-size: 0.9rem;
      color: #555;
    }
  }
`;

export const VoiceOptionGroup = styled.div`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  label {
    display: block;
    font-size: 0.9rem;
    font-weight: 500;
    color: #555;
    margin-bottom: 0.25rem;
  }
`;

export const VoiceOptionsField = styled.div`
  margin-bottom: 1rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
  }
  
  select {
    width: 100%;
    padding: 0.5rem;
    border-radius: 4px;
    border: 1px solid #ddd;
  }
`;

export const LanguageNotice = styled.p`
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #eee;
  font-size: 0.8rem;
  color: #777;
  font-style: italic;
  text-align: center;
`;

/*
 * Static images (photos, screenshots). Use `&&` to outrank the generic
 * `.Content img` rule above. Width is left to the image's natural size but
 * capped to the column width, and the height is capped so tall/large images
 * never tower over the reader.
 */
export const BlogPostImage = styled.img`
  && {
    display: block;
    width: auto;
    max-width: 100%;
    height: auto;
    max-height: 70vh;
    margin: 2rem auto;
    border-radius: 8px;
    transition: transform 0.3s ease;
  }

  &&:hover {
    transform: scale(1.01);
  }

  @media (max-width: 768px) {
    && {
      max-height: none;
      margin: 1.5rem auto;
    }
  }
`;

/*
 * Animated GIFs are usually small screen captures. Rendered at their native
 * size they look tiny next to the text, so scale them up to a comfortable
 * reading width (capped so large GIFs do not blow out the column) and centre
 * them. No max-height cap -- GIFs are typically wide demos, not tall photos.
 */
export const BlogPostGif = styled.img`
  && {
    display: block;
    width: min(100%, 680px);
    height: auto;
    margin: 2rem auto;
    border-radius: 8px;
    transition: transform 0.3s ease;
  }

  &&:hover {
    transform: scale(1.01);
  }

  @media (max-width: 768px) {
    && {
      width: 100%;
      margin: 1.5rem auto;
    }
  }
`;